import React, { useEffect, useRef, useState } from "react";
import { Card, Spinner, Form, Row, Col } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileDownload } from "@fortawesome/free-solid-svg-icons";
import { Button, ButtonGroup } from "@themesberg/react-bootstrap";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { fetchApi } from "../../../redux/api";
import { formatNumber } from "../../../redux/utils";
import moment from "moment";

const options = [
  { value: "capital_receipts_by_item", title: "Capital Rec By Item" },
];

const isEmpty = (v) =>
  v === 0 || v === null || v === undefined || v === "--" || v === "0.00" || v === "";

export const newFilter = (item) =>
  ["prev_year2_actual_amount", "prev_year1_approve_amount", "prev_year1_actual_amount"].every((k) => isEmpty(item[k]));

export default function CapitalRecByItem() {
  const [results, setResults] = useState([]);
  const [form, setForm] = useState({
    budget_year: moment().format("YYYY"),
  });
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(options[0]);
  const years = Array.from({ length: 10 }, (_, i) =>
    moment().format("YYYY") - i
  );
  const tableRef = useRef(null);

  const handleChange = ({ target: { name, value } }) => {
    setForm((p) => ({ ...p, [name]: value }));
  };

  useEffect(() => {
    setLoading(true);
    fetchApi(
      `reports?query_type=${selectedOption.value}&budget_year=${form.budget_year}`
    )
      .then((data) => {
        const rows = data?.results || [];
        setResults(rows.filter(
          (i) =>
            i.prev_year2_actual_amount != null ||
            i.prev_year1_actual_amount != null ||
            i.prev_year1_approve_amount != null
        ));
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  }, [form.budget_year, selectedOption.value]);

  const totalPrevYear2 = results.reduce(
    (sum, r) => sum + parseFloat(r.prev_year2_actual_amount || 0), 0
  );
  const totalPrevYear1Approve = results.reduce(
    (sum, r) => sum + parseFloat(r.prev_year1_approve_amount || 0), 0
  );
  const totalPrevYear1Actual = results.reduce(
    (sum, r) => sum + parseFloat(r.prev_year1_actual_amount || 0), 0
  );
  const totalBudget = results.reduce(
    (sum, r) => sum + parseFloat(r.budget_amount || 0), 0
  );
  const totalApprove = results.reduce(
    (sum, r) => sum + parseFloat(r.approve_amount || 0), 0
  );
  const totalAdjustment = results.reduce(
    (sum, r) => sum + parseFloat(r.supplementary_amount || 0), 0
  );

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>{selectedOption.title}</h4>
        </div>
      </div>

      <Card border="light" className="shadow-sm">
        <Card.Body>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Budget Year</Form.Label>
                <Form.Select
                  name="budget_year"
                  value={form.budget_year}
                  onChange={handleChange}
                >
                  <option>--select--</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} className="text-center">
              <ButtonGroup>
                {options.map((item) => (
                  <Button
                    size="sm"
                    key={item.title}
                    variant={
                      selectedOption.title === item.title
                        ? "primary"
                        : "outline-secondary"
                    }
                    onClick={() => setSelectedOption(item)}
                  >
                    <small>{item.title}</small>
                  </Button>
                ))}
              </ButtonGroup>
            </Col>
            <Col
              md={3}
              className="d-flex align-items-end justify-content-end"
            >
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  if (tableRef.current) {
                    const ws = XLSX.utils.table_to_sheet(tableRef.current);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Capital Rec By Item");
                    const buf = XLSX.write(wb, {
                      bookType: "xlsx",
                      type: "array",
                    });
                    FileSaver.saveAs(
                      new Blob([buf], {
                        type: "application/octet-stream",
                      }),
                      `${selectedOption.title}.xlsx`
                    );
                  }
                }}
              >
                <FontAwesomeIcon icon={faFileDownload} className="me-1" />{" "}
                Download Excel
              </Button>
            </Col>
          </Row>

          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <table
                className="table table-bordered table-striped"
                ref={tableRef}
                style={{ fontSize: "13px" }}
              >
                <thead>
                  <tr
                    style={{
                      fontSize: "12px",
                      textAlign: "center",
                      backgroundColor: "orange",
                    }}
                  >
                    <th style={{ backgroundColor: "orange" }}>
                      Receipt Description
                    </th>
                    <th style={{ backgroundColor: "orange" }}>
                      Administrative Code and Description
                    </th>
                    <th style={{ backgroundColor: "orange" }}>
                      Economic Code and Description
                    </th>
                    <th style={{ backgroundColor: "orange" }}>
                      Fund Code and Description
                    </th>
                    <th style={{ backgroundColor: "orange" }}>{`${
                      parseInt(form.budget_year) - 2
                    } Actual(₦)`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${
                      parseInt(form.budget_year) - 1
                    } Approved(₦)`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${
                      parseInt(form.budget_year) - 1
                    } Actual(₦)`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${parseInt(
                      form.budget_year
                    )} Proposed Budget`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${parseInt(
                      form.budget_year
                    )} Adjustments Budget`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${parseInt(
                      form.budget_year
                    )} Approved Budget`}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ fontSize: "12px", fontWeight: "bold" }}>
                    <td>{selectedOption.title}</td>
                    <td />
                    <td />
                    <td />
                    <td style={{ textAlign: "right" }}>
                      {formatNumber(totalPrevYear2)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatNumber(totalPrevYear1Approve)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatNumber(totalPrevYear1Actual)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatNumber(totalBudget)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatNumber(totalAdjustment)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatNumber(totalApprove)}
                    </td>
                  </tr>
                  {results.map((item, idx) => {
                    if (newFilter(item)) return null;
                    return (
                      <tr
                        key={`${item.code}_${item.mda_code}_${idx}`}
                        style={{ fontSize: "11px" }}
                      >
                        <td>{item.description || ""}</td>
                        <td>
                          {[item.mda_name, item.mda_code]
                            .filter(Boolean)
                            .join(" - ")}
                        </td>
                        <td>
                          {[item.code, item.description]
                            .filter(Boolean)
                            .join(" - ")}
                        </td>
                        <td>
                          {[item.fund_code, item.fund_description]
                            .filter(Boolean)
                            .join(" - ")}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {formatNumber(item.prev_year2_actual_amount)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {formatNumber(item.prev_year1_approve_amount)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {formatNumber(item.prev_year1_actual_amount)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {formatNumber(item.budget_amount)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {formatNumber(item.supplementary_amount)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {formatNumber(item.approve_amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
}
